import { giphyDarkGrey, giphyIndigo } from '@giphy/js-brand'
import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router'
import CSRFToken from 'shared/components/form/csrftoken-hoc'
import { AdaptedFormInput } from 'shared/components/form/fields'
import {
    Content,
    CTAButton as BrandCTAButton,
    FormContainer,
    MobileBackButton,
    Title,
} from 'shared/components/form/form-components'
import styled from 'styled-components'
import ShowHidePasswordIcon from '../../../shared/components/form/show-hide-password-icon'
import CloseIcon from '../../../shared/components/icons/svg/close-icon'
import checkStrength from '../../../shared/util/password'

const CTAButton = styled(BrandCTAButton)`
    width: 100%;
`

export const CURRENT_PASSWORD = 'current_password'
export const NEW_PASSWORD = 'new_password'
export const NEW_PASSWORD_CONFIRM = 'new_password_confirm'

const PASSWORD_STRENGTH_ERROR_MESSAGE =
    'Passwords must be at least 8 characters, and contain 2 of the following criteria: lowercase letters, uppercase letters, numbers, symbols'
const PASSWORD_NO_MATCH_ERROR_MESSAGE = 'Passwords do not match'

type Errors = {
    current_password?: string
    new_password?: string
    new_password_confirm?: string
}

const InputWrapper = styled.div`
    position: relative;
`

const StyledShowHidePasswordIcon = styled(ShowHidePasswordIcon)`
    bottom: 6px;
`

const PasswordResetForm = ({ csrftoken }) => {
    const [currentPassword, setCurrentPassword] = useState<string>('')
    const [newPassword, setNewPassword] = useState<string>('')
    const [newPasswordConfirmation, setNewPasswordConfirmation] = useState<string>('')

    const [currentPasswordError, setCurrentPasswordError] = useState<Errors>({
        [CURRENT_PASSWORD]: 'This field is required',
        [NEW_PASSWORD]: '',
        [NEW_PASSWORD_CONFIRM]: '',
    })

    const [shownErrors, setShownErrors] = useState({})
    const [showPassword, setShowPassword] = useState<boolean>(false)

    const form = useRef<HTMLFormElement | null>(null)
    const { token } = useParams<{ token: string }>()
    const showCurrentPassword = !token
    const submit = async (e) => {
        e.stopPropagation()
        e.preventDefault()
        form?.current?.submit()
    }

    const onBlur = ({ target: { name } }) => {
        setShownErrors((errors) => ({ ...errors, [name]: true }))
    }

    useEffect(() => {
        if (newPassword.length && newPasswordConfirmation.length) {
            if (newPassword !== newPasswordConfirmation) {
                setCurrentPasswordError((prevError) => ({
                    ...prevError,
                    [NEW_PASSWORD_CONFIRM]: PASSWORD_NO_MATCH_ERROR_MESSAGE,
                }))
            } else {
                setCurrentPasswordError((prevError) => {
                    delete prevError[NEW_PASSWORD_CONFIRM]
                    return prevError
                })
            }
        }

        if (checkStrength(newPassword) < 2) {
            setCurrentPasswordError((prevError) => ({ ...prevError, [NEW_PASSWORD]: PASSWORD_STRENGTH_ERROR_MESSAGE }))
        } else {
            setCurrentPasswordError((prevError) => {
                delete prevError[NEW_PASSWORD]
                return prevError
            })
        }

        if (showCurrentPassword && !currentPassword.length) {
            setCurrentPasswordError((prevError) => ({ ...prevError, [CURRENT_PASSWORD]: 'Required Field' }))
        } else {
            setCurrentPasswordError((prevError) => {
                delete prevError[CURRENT_PASSWORD]
                return prevError
            })
        }
    }, [newPassword, newPasswordConfirmation, currentPassword])

    const onChange = ({ target: { name, value } }) => {
        setShownErrors((errors) => ({ ...errors, [name]: false }))

        if (name === NEW_PASSWORD) {
            setNewPassword(value)
        }

        if (name === NEW_PASSWORD_CONFIRM) {
            setNewPasswordConfirmation(value)
        }

        if (name === CURRENT_PASSWORD) {
            setCurrentPassword(value)
        }
    }

    const disabled = !!Object.keys(currentPasswordError).length

    return (
        <FormContainer>
            <form onSubmit={submit} ref={form} method="post">
                {csrftoken && <input type="hidden" name="csrfmiddlewaretoken" value={csrftoken} />}
                {!showCurrentPassword && <input id="token" name="token" type="hidden" value={token} />}
                <Content>
                    <Title>
                        {showCurrentPassword ? `Change` : `Reset `} <br />
                        Password
                    </Title>
                    {showCurrentPassword && (
                        <AdaptedFormInput
                            inputProps={{
                                autoComplete: 'current-password',
                                name: CURRENT_PASSWORD,
                                placeholder: 'Current Password',
                                onBlur,
                                onChange,
                                type: 'password',
                                value: currentPassword,
                            }}
                            errorTooltip
                            errorMessage={shownErrors[CURRENT_PASSWORD] ? currentPasswordError[CURRENT_PASSWORD] : null}
                        />
                    )}
                    <InputWrapper>
                        <AdaptedFormInput
                            inputProps={{
                                autoComplete: 'new-password',
                                name: NEW_PASSWORD,
                                placeholder: 'New Password',
                                type: showPassword ? 'text' : 'password',
                                onBlur,
                                onChange,
                                value: newPassword,
                            }}
                            errorTooltip
                            errorMessage={shownErrors[NEW_PASSWORD] ? currentPasswordError[NEW_PASSWORD] : null}
                        />
                        <StyledShowHidePasswordIcon
                            onClick={() => setShowPassword(!showPassword)}
                            show={showPassword}
                        />
                    </InputWrapper>
                    <InputWrapper>
                        <AdaptedFormInput
                            inputProps={{
                                autoComplete: 'new-password',
                                name: NEW_PASSWORD_CONFIRM,
                                placeholder: 'Confirm New Password',
                                type: showPassword ? 'text' : 'password',
                                value: newPasswordConfirmation,
                                onBlur,
                                onChange,
                            }}
                            errorTooltip
                            errorMessage={
                                shownErrors[NEW_PASSWORD_CONFIRM] ? currentPasswordError[NEW_PASSWORD_CONFIRM] : null
                            }
                        />
                        <StyledShowHidePasswordIcon
                            onClick={() => setShowPassword(!showPassword)}
                            show={showPassword}
                        />
                    </InputWrapper>
                    <CTAButton color={disabled ? giphyDarkGrey : giphyIndigo} disabled={disabled}>
                        Save
                    </CTAButton>
                </Content>
            </form>
            <MobileBackButton onClick={() => history.back()}>
                <CloseIcon width={20} height={20} />
            </MobileBackButton>
        </FormContainer>
    )
}

export default CSRFToken(PasswordResetForm)
